<script lang="ts">
import { defineComponent as DO_defineComponent } from 'vue';
export default /*#__PURE__*/ DO_defineComponent({
	name: 'ToTooltip'
});
</script>
<template>
	<div v-if="lazy ? show : true" ref="tooltip" :flex="flex ? flex : null" class="to-tooltip" :class="setClass" :style="setStyle">
		<div ref="arrow" class="to-tooltip-arrow"></div>
		<div ref="content" class="to-tooltip-content">
			<div class="to-tooltip-content-text">
				<slot></slot>
			</div>
		</div>
	</div>
</template>
<script setup lang="ts">
import { ref, computed, onDeactivated, onBeforeUnmount, nextTick, getCurrentInstance } from 'vue'
import { tooltipProps, tooltipEmits } from './tooltip'

const props = defineProps(tooltipProps)
const emit = defineEmits(tooltipEmits)
const blurable = ref(false)
const ialign = ref('')
const iarrow = ref('')
const iposition = ref('')
const show = ref(false)
const timer = ref(null)
const link = ref(null)

// dom
const arrow = ref(null)
const content = ref(null)
const scrollNode = ref(null)
const tooltip = ref(null)
const instance = getCurrentInstance()

const setClass = computed(() => {
	let arr = []
	if (props.color) {
		arr.push(`color-${props.color}`)
	}
	if (iposition.value) {
		arr.push(`position-${iposition.value}`)
	}
	if (props.mode) {
		arr.push(`mode-${props.mode}`)
	}
	if (props.readonly) {
		arr.push('is-readonly')
	}
	if (iarrow.value) {
		arr.push(`arrow-${iarrow.value}`)
	}
	if (show.value) {
		arr.push('is-show')
	}
	return arr
})
const setStyle = computed(() => {
	let obj: {
		width: string
	} = {
		width: ''
	}
	if (props.width) {
		if (!isNaN(props.width as number)) {
			obj.width = props.width + 'em'
		} else if (props.width === 'full') {
			obj.width = '100%'
		} else {
			obj.width = props.width as string
		}
	}
	return obj
})
onDeactivated(() => {
	// 组件销毁时销毁事件
	destroy()
})
onBeforeUnmount(() => {
	// 组件销毁时销毁事件
	destroy()
})
const destroy = () => {
	// 销毁事件绑定
	if (document.body && document.body.removeEventListener) {
		document.body.removeEventListener('click', blur)
	}

	if (scrollNode.value && scrollNode.value.removeEventListener) {
		scrollNode.value.removeEventListener('scroll', close)
	}

	// 销毁移动过的 DOM
	// if (this.$el.parentNode) {
	// 	this.$el.parentNode.removeChild(this.$el)
	// }

	if (timer.value) {
		clearTimeout(timer.value)
		timer.value = null
	}
}
const set = link => {
	// 整体参数
	const bodyHeight = document.body.clientHeight
	const bodyWidth = document.body.clientWidth

	// 目标参数
	const linkHeight = link.offsetHeight
	const linkWidth = link.offsetWidth
	const linkTop = link.getBoundingClientRect().top
	const linkLeft = link.getBoundingClientRect().left
	const linkBottom = bodyHeight - linkTop - linkHeight
	const linkRight = bodyWidth - linkLeft - linkWidth
	const linkX = linkLeft + linkWidth / 2
	const linkY = linkTop + linkHeight / 2
	const arrowSize = arrow.value.offsetHeight

	// 提示参数
	let tipWidth = content.value.offsetWidth
	let tipHeight = content.value.offsetHeight

	// 已定义直接取值
	if (props.position !== 'auto') {
		iposition.value = props.position
	}

	// 未定义位置则需要计算从上还是从下弹出
	else {
		// 顶部空间足够则从顶部显示
		if (linkTop >= tipHeight + arrowSize / 2) {
			iposition.value = 'top'
		}

		// 底部空间足够则从底部显示
		else if (linkBottom >= tipHeight + arrowSize / 2) {
			iposition.value = 'bottom'
		}

		// 顶部底部空间都不足够时，放在文档顶部
		else {
			iposition.value = 'top-ab'
		}
	}

	// 计算提示对齐方式
	if (props.align !== 'auto') {
		ialign.value = props.align
	} else {
		// 位置在上下的情况
		if (iposition.value === 'top' || iposition.value === 'bottom') {
			// 不能居中完整显示则看哪边空间大
			if ((tipWidth / 2 > linkLeft + linkWidth / 2 || tipWidth / 2 > linkRight + linkWidth / 2) && tipWidth > linkWidth) {
				if (linkLeft > linkRight) {
					ialign.value = 'right'
				} else {
					ialign.value = 'left'
				}
			}

			// 能居中完整显示则居中完整显示
			else {
				ialign.value = 'center'
			}
		}

		// 位置在左右的情况
		else {
			// 不能居中完整显示则看哪边空间大
			if ((tipHeight / 2 > linkTop + linkHeight / 2 || tipHeight / 2 > linkBottom + linkHeight / 2) && tipHeight > linkHeight) {
				if (linkTop > linkBottom) {
					ialign.value = 'bottom'
				} else {
					ialign.value = 'top'
				}
			}

			// 能居中完整显示则居中完整显示
			else {
				ialign.value = 'middle'
			}
		}
	}

	// 计算箭头对齐方式
	if ((ialign.value === 'top' || ialign.value === 'bottom' || ialign.value === 'middle') && tipHeight < linkHeight) {
		iarrow.value = 'middle'
	} else if ((ialign.value === 'left' || ialign.value === 'right' || ialign.value === 'center') && tipWidth < linkWidth) {
		iarrow.value = 'center'
	} else {
		iarrow.value = ialign.value
	}

	// 计算位置
	nextTick(() => {
		// 因为 position 会导致 tip 高宽的变化，设置前需要重新获取一次
		tipWidth = tooltip.value.offsetWidth
		tipHeight = tooltip.value.offsetHeight
		arrow.value.style.left = ''
		arrow.value.style.right = ''
		arrow.value.style.top = ''
		arrow.value.style.bottom = ''
		tooltip.value.style.left = ''
		tooltip.value.style.right = ''
		tooltip.value.style.top = ''
		tooltip.value.style.bottom = ''

		if (iposition.value === 'top-ab') {
			tooltip.value.style.top = 0
		}
		if (iposition.value === 'top') {
			tooltip.value.style.top = linkTop - tipHeight + 'px'
		}
		if (iposition.value === 'bottom') {
			tooltip.value.style.top = linkTop + linkHeight + 'px'
		}
		if (iposition.value === 'left') {
			tooltip.value.style.left = linkLeft - tipWidth + 'px'
		}
		if (iposition.value === 'right') {
			tooltip.value.style.left = linkLeft + linkWidth + 'px'
		}

		if (ialign.value === 'left') {
			tooltip.value.style.left = linkLeft + 'px'
			arrow.value.style.left = linkWidth / 2 - arrowSize / 2 + 'px'
		}
		if (ialign.value === 'right') {
			tooltip.value.style.left = bodyWidth - tipWidth - linkRight + 'px'
			arrow.value.style.right = linkWidth / 2 - arrowSize / 2 + 'px'
		}
		if (ialign.value === 'center') {
			tooltip.value.style.left = linkX - tipWidth / 2 + 'px'
			arrow.value.style.left = ''
			arrow.value.style.right = ''
		}
		if (ialign.value === 'top') {
			tooltip.value.style.top = linkTop + 'px'
			arrow.value.style.top = linkHeight / 2 - arrowSize / 2 + 'px'
		}
		if (ialign.value === 'bottom') {
			tooltip.value.style.top = linkTop - tipHeight + linkHeight + 'px'
			arrow.value.style.bottom = linkHeight / 2 - arrowSize / 2 + 'px'
		}
		if (ialign.value === 'middle') {
			tooltip.value.style.top = linkY - tipHeight / 2 + 'px'
			arrow.value.style.top = ''
			arrow.value.style.bottom = ''
		}

		if (iarrow.value === 'left') {
			arrow.value.style.left = linkWidth / 2 - arrowSize / 2 + 'px'
		}
		if (iarrow.value === 'right') {
			arrow.value.style.right = linkWidth / 2 - arrowSize / 2 + 'px'
		}
		if (iarrow.value === 'center') {
			arrow.value.style.left = ''
			arrow.value.style.right = ''
		}
		if (iarrow.value === 'top') {
			arrow.value.style.top = linkHeight / 2 - arrowSize / 2 + 'px'
		}
		if (iarrow.value === 'bottom') {
			arrow.value.style.bottom = linkHeight / 2 - arrowSize / 2 + 'px'
		}
		if (iarrow.value === 'middle') {
			arrow.value.style.top = ''
			arrow.value.style.bottom = ''
		}
	})
}
const open = by => {
	// 延时显示
	if (props.delay) {
		if (timer.value) {
			clearTimeout(timer.value)
			timer.value = null
		}
		timer.value = setTimeout(() => {
			openHandle(by)
		}, props.delay as number)
	} else {
		openHandle(by)
	}
}
const openHandle = by => {
	emit('beforeOpen')
	blurable.value = false
	if (!by) {
		return
	}
	link.value = by
	show.value = true
	nextTick(() => {
		if (!tooltip.value) {
			return
		}

		// 点击层外关闭
		document.body.addEventListener('click', blur, true)
		set(by)
		if (link.value && props.closeOnScroll) {
			setScrollStatus()
		}
		// 设置 z-index
		if (props.zIndex) {
			;(instance?.proxy?.$el as HTMLElement).style.zIndex = props.zIndex.toString()
		} else {
			;(instance?.proxy?.$el as HTMLElement).style.zIndex = (window as any).$TouchUI?.zIndex.toString()
			;(window as any).$TouchUI ? (window as any).$TouchUI.zIndex++ : ((window as any).$TouchUI = { zIndex: 1 })
		}
	})

	blurable.value = true

	// // 防止点开后立即因为 blur 事件而关闭掉
	// let timer = null
	// timer = setTimeout(() => {
	// 	blurable.value = true
	// 	clearTimeout(timer)
	// 	timer = null
	// }, 100)

	// 显示
	emit('opened')
}
// type?: number
const close = () => {
	// && type !== 1
	if (!tooltip.value || !blurable.value) return
	if (props.delay) {
		if (timer.value) {
			clearTimeout(timer.value)
			timer.value = null
		}
		timer.value = setTimeout(() => {
			closeHandle()
		}, Number(props.delay))
	} else {
		closeHandle()
	}
}
const closeHandle = () => {
	show.value = false
	if (scrollNode.value && scrollNode.value.removeEventListener) {
		scrollNode.value.removeEventListener('scroll', close)
	}
}
const setScrollStatus = () => {
	const searchNode = Node => {
		if (Node.nodeName === 'BODY') {
			return Node
		} else {
			if (Node.nodeName === 'DIV' && typeof Node.className === 'string' && Node.className.indexOf('to-scroll-content') > -1) {
				return Node
			} else {
				return searchNode(Node.parentNode)
			}
		}
	}
	scrollNode.value = searchNode(link.value.parentNode)

	if (scrollNode.value) {
		// 鼠标滚动时，直接关闭
		// 设置延时是因为表单校验的时候，获取焦点同时要展示 tooltip，获取焦点的对象如果只有半边在视角以内，则会自动触发滚动条滚动事件
		let timer = null
		timer = setTimeout(() => {
			scrollNode.value.addEventListener('scroll', close)
			clearTimeout(timer)
			timer = null
		}, 0)
	}
}
// 点击层外关闭，通过递归判断点击是否在绑定此对象实例的 DOM 之内
const blur = e => {
	if (!tooltip.value || !blurable.value || !props.closeOnBlur) return
	nextTick(() => {
		let node = e.target
		while (node && node !== tooltip.value && node !== link.value && node.nodeName !== 'BODY') {
			node = node.parentNode
		}
		if (node && node.nodeName === 'BODY') {
			close()
		}
	})
}
// 暴露方法
defineExpose({ open, close })
</script>
